import posthog from 'posthog-js'
import { PosthogEvent } from '@/types/posthog'
import { useUser } from '@clerk/clerk-react'
import { useMedicationRequestStore } from '@/lib/stores/medication-request'
import { useFlag } from '@unleash/proxy-client-react'
import {
  getDecisionAssistantActionAnalytics,
  getDecisionAssistantViewAnalytics
} from '@/lib/analytics/decision-assistant-analytics'

export function usePosthogCustom() {
  const { user } = useUser()
  const daFlagEnabled = useFlag('da-pa-suggestion-panel')
  const isJuniorPrescriber = !!user?.publicMetadata.roles?.includes('JUNIOR_PRESCRIBER')

  const { sessionStartTime, resetSessionStartTime } = useMedicationRequestStore()

  const daContext = { daFlagEnabled }

  const trackMedRequestAction = (action: 'approve' | 'reject' | 'contact', medReqId: string) => {
    const event =
      action === 'approve'
        ? PosthogEvent.MED_REQUEST_APPROVED
        : action === 'contact'
          ? PosthogEvent.MED_REQUEST_CONTACT
          : PosthogEvent.MED_REQUEST_REJECTED

    const secondsToAction = Math.round((new Date().getTime() - sessionStartTime.getTime()) / 1000)

    posthog?.capture(event, {
      med_req_id: medReqId,
      seconds_to_action: secondsToAction,
      prescriber: user?.fullName,
      isJuniorPrescriber,
      ...getDecisionAssistantActionAnalytics(medReqId, daContext)
    })
  }

  const trackTimeSpent = (duration: number) => {
    posthog?.capture(PosthogEvent.TIME_SPENT, {
      duration,
      date: new Date().toISOString().split('T')[0],
      prescriber: user?.fullName
    })
  }

  const trackMedRequestViewed = (medReqId: string) => {
    resetSessionStartTime()

    posthog?.capture(PosthogEvent.MED_REQUEST_VIEWED, {
      med_req_id: medReqId,
      prescriber: user?.fullName,
      isJuniorPrescriber,
      ...getDecisionAssistantViewAnalytics(medReqId, daContext)
    })
  }

  const trackBatchApproval = (medReqId: string, sessionStartTimeBatch: Date) => {
    const secondsToAction = Math.round((new Date().getTime() - sessionStartTimeBatch.getTime()) / 1000)

    posthog?.capture(PosthogEvent.MED_REQUEST_BATCH_APPROVED, {
      med_req_id: medReqId,
      seconds_to_action: secondsToAction,
      prescriber: user?.fullName
    })
  }

  const trackMedRequestBatchViewed = (medReqId: string) => {
    posthog?.capture(PosthogEvent.MED_REQUEST_BATCH_VIEWED, {
      med_req_id: medReqId,
      prescriber: user?.fullName
    })
  }

  return {
    trackMedRequestAction,
    trackTimeSpent,
    trackMedRequestViewed,
    trackBatchApproval,
    trackMedRequestBatchViewed
  }
}
